import React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const SEO = ({
  title,
  description,
  image,
  slug,
  locale,
  homepage,
  appData,
  seoCompose,
  pageTemplate,
  trialPage,
  robotsSettings
}) => {
  const data = useStaticQuery(seoQuery)
  const pageType = pageTemplate || "Page"
  if (appData) {
    var { siteTitle, siteDescription, twitterUsername } = appData
  } else {
    var siteTitle = ""
    var siteDescription = ""
    var twitterUsername = ""
  }

  let siteShareImage = appData?.siteShareImage?.file?.url
  if (!siteShareImage?.startsWith("http")) {
    siteShareImage = "https:" + siteShareImage
  }

  const { siteURL } = data?.site?.siteMetadata

  const seo = {
    title: seoCompose?.title
      ? `${seoCompose?.title}${siteTitle ? ` | ${siteTitle}` : ""}`
      : title
      ? `${title}${siteTitle ? ` | ${siteTitle}` : ""}`
      : "",
    description: seoCompose?.description || description || siteDescription,
    image: image || siteShareImage,
    locale: (locale && locale != 'en-US') ? locale : "en",
  }
  const allLocales = data.allContentfulLanguage?.edges.map(({ node }) => {
    if (node?.node_locale.toLowerCase() === "en-us") {
      node.node_locale = "en"
    }
    return node.node_locale
  })

  const openGraph = {
    title: seoCompose?.opengraphTitle || seo?.title,
    type: seoCompose?.opengraphType,
    image: seoCompose?.opengraphImage?.image?.file?.url
      ? `https:${seoCompose?.opengraphImage?.image?.file?.url}`
      : seo?.image,
    description: seoCompose?.opengraphDescription || seo?.description,
    url: seoCompose?.opengraphUrl || seo?.url,
  }

  const twitter = {
    title: seoCompose?.twitterTitle || seo.title,
    image: seoCompose?.twitterImage?.image?.file?.url
      ? `https:${seoCompose?.twitterImage?.image?.file?.url}`
      : seo?.image,
    description: seoCompose?.twitterDescription || seo?.description,
  }

  const onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index
  }

  const hreflang = seoCompose?.hreflang || []
  const locales = allLocales?.filter(onlyUnique)

  return (
    <>
      <html lang="en" />
      <link
        rel="canonical"
        href={
          slug === homepage
          ? `${siteURL}/${seo.locale}`
          : pageType && pageType === "Product"
          ? `${siteURL}/${seo.locale}/products/${slug}`
          : pageType && pageType === "BlogArticle"
          ? `${siteURL}/${seo.locale}/blog/${slug}`
          : pageType && pageType === "productTrialPage"
          ? `${siteURL}${slug}`
          : `${siteURL}/${seo.locale}/${slug}`
        }
      />
      <title>{seo?.title}</title>
      {hreflang
        ? hreflang?.map((hreflang, idx) => (
            <link
              key={idx}
              rel="alternate"
              hrefLang={hreflang.key}
              href={hreflang.value}
            />
          ))
        : locales.map((locale, idx) => (
            <link
              key={idx}
              rel="alternate"
              hrefLang={locale}
              href={
                slug === homepage
                  ? `${siteURL}/${locale}`
                  : pageType === "Product"
                  ? `${siteURL}/${locale}/products/${slug}`
                  : `${siteURL}/${locale}/${slug}`
              }
            />
          ))}
      <meta name="description" content={seo.description} />
      {robotsSettings && <meta name="robots" content={`${robotsSettings?.no_follow ? 'nofollow': ''} ${(robotsSettings?.no_index || process.env.GATSBY_ENV !== "production") ? 'noindex': ''}`} />}
      {openGraph?.image && <meta name="image" content={`${openGraph?.image}`} />}
      {openGraph?.url && <meta property="og:url" content={`${openGraph?.url}`} />}
      {openGraph?.title && (
        <meta property="og:title" content={openGraph?.title} />
      )}
      {openGraph?.type && <meta property="og:type" content={openGraph?.type} />}
      {openGraph?.description && (
        <meta property="og:description" content={openGraph?.description} />
      )}
      {openGraph?.image && (
        <meta property="og:image" content={`${openGraph?.image}`} />
      )}

      {twitterUsername && (
        <meta name="twitter:creator" content={twitterUsername} />
      )}
      {twitter?.title && <meta name="twitter:title" content={twitter?.title} />}
      {twitter?.description && (
        <meta name="twitter:description" content={twitter?.description} />
      )}
      {twitter?.image && <meta name="twitter:image" content={twitter?.image} />}
    </>
  )
}

const seoQuery = graphql`
  query SEO {
    site {
      siteMetadata {
        siteURL
      }
    }
    allContentfulLanguage {
      edges {
        node {
          node_locale
        }
      }
    }
  }
`

export default SEO

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
}
SEO.defaultProps = {
  title: null,
  description: null,
  image: null,
}
